/**
 * The re-seal (#1694, spec 6.12 step 2): every row the member has, moved from
 * the committed generation to the pending one — or, while the rotation is being
 * unwound, back the other way (#1730, api#1148). One loop, two directions: the
 * only thing that changes is which generation is being emptied, because the
 * sealing hook and the write gate both follow the same flag.
 *
 * ── WHY THIS IS SO SMALL ───────────────────────────────────────────────────
 *
 * It performs no crypto. The sealing hook seals on the way out, under whichever
 * generation `courses-keys/writeGeneration` names — and once `rotate/begin`
 * succeeds that is MK2, because `EncryptionModeGuard` refuses any other `kid`
 * (api#1133). So "re-seal a row" is "write it back", exactly as the migration
 * writes a plaintext row back, and this module is a filter and a loop.
 *
 * ── WHERE EACH FACT COMES FROM, AND WHY NOT THE OTHER PLACE ────────────────
 *
 * WHICH ROWS REMAIN comes from `GET /rows` (api#1128), not from looking at the
 * rows themselves. The read path hands back OPENED rows — the envelope, and
 * with it the `kid`, is gone by the time anything here sees them — so there is
 * nothing local to filter on. `GET /rows` is also the only member-wide answer
 * for blank answers, which is why api#1128 exists at all.
 *
 * THE CONTENT comes from the ordinary reads, because `GET /rows` returns ids
 * and revisions and never payloads: the re-sealing device already holds the
 * plaintext, and shipping ciphertext back would double the traffic to say
 * nothing new. Blank answers need their program/lesson/block scope to be
 * written at all, and only the enumeration has it.
 *
 * WHAT IS LEFT is the server's `remaining`, the same count `rotate/commit`
 * refuses on — so progress and predicate cannot disagree, and a walk cannot
 * believe itself finished when the server would not.
 *
 * ── RESUMING ───────────────────────────────────────────────────────────────
 *
 * Nothing is remembered between runs. A row that has been re-sealed stops
 * matching `GET /rows?kid=<old>` the moment it is written, so a second pass
 * simply collects less than the first. Killing the app mid-walk and reopening
 * costs the pages it had not reached and nothing else.
 */
import { UNREADABLE_REASON, UNRECOVERABLE_FIELD } from '@/constants/courses-encryption';
import { cameFromEnvelope } from '@/utils/courses-annotations-read-path';
import { ANNOTATION_TYPE } from '@/utils/courses-annotations-transport';
import { collectRows, rewriteRow } from './migration';
import { SEALED_FIELDS } from './sealed-writes';

/**
 * ⚠️ THE TWO SIDES SPELL THE FOUR TABLES DIFFERENTLY, and joining on the wrong
 * spelling is silent.
 *
 * The API answers `GET /rows` with `blankAnswer`; this app calls the same thing
 * `blank` (`ANNOTATION_TYPE`). The walk matches the server's rows against the
 * local corpus by `type:id`, so before this map every blank answer failed to
 * match, was never re-sealed, and left `remaining` above zero for ever — which
 * is a rotation that can never commit, on any member who has ever filled in a
 * blank. Every other device stays read-only for the life of it.
 *
 * Mapped rather than renamed on either side: the API's name matches its Prisma
 * model and this app's matches its transport, and both are load-bearing
 * elsewhere. What must not happen again is the mismatch being SILENT, which is
 * what `assertKnownType` below is for.
 */
const TYPE_FROM_API = Object.freeze({
  note: ANNOTATION_TYPE.NOTE,
  highlight: ANNOTATION_TYPE.HIGHLIGHT,
  tag: ANNOTATION_TYPE.TAG,
  blankAnswer: ANNOTATION_TYPE.BLANK,
});

/**
 * A row type this build does not know is not a row to skip: it is a row that
 * would sit on the old generation for ever while the walk reported progress.
 * Better to fail the rotation loudly than to leave one that cannot finish.
 */
function assertKnownType(type) {
  const local = TYPE_FROM_API[type];
  if (!local) {
    throw new TypeError(
      `resealRemaining: the API named a row type this build cannot re-seal ("${type}")`,
    );
  }
  return local;
}

/** Spec 6.7's batch size for the migration, and the same reasons apply here. */
export const RESEAL_BATCH_SIZE = 20;

/**
 * A bound on the paging, not a limit on the member: the pages are 200 rows and
 * a walk that asked more than this has stopped making progress, which is worth
 * ending rather than looping on.
 */
export const RESEAL_MAX_PAGES = 200;

/**
 * How many collect-and-write rounds one call may run — the same shape, and the
 * same reason, as the migration's.
 *
 * TWO IS THE ORDINARY COURSE: one to write, one to see nothing left. A single
 * round cannot tell "finished" from "finished a moment ago" — the count is
 * four filtered queries taken right after a batch of writes, and it came back
 * as one row outstanding on a rotation whose rows had in fact all moved
 * (COURSES-E2EE-014, 2026-09-08). The rotation then sat there, unable to
 * commit, on a count that was already stale when it was read.
 *
 * The third round is for rows another device added between the first two. The
 * loop leaves the moment a round moves nothing, so a device with a row that
 * genuinely will not write pays two rounds rather than three.
 */
export const RESEAL_MAX_PASSES = 3;

export const RESEAL_OUTCOME = Object.freeze({
  /** Nothing was left on the old generation. */
  DONE: 'done',
  /** Some rows moved; others did not, and the next pass tries again. */
  PARTIAL: 'partial',
  /** No rotation is running, so there is nothing to re-seal. */
  NOT_ROTATING: 'not-rotating',
});

/**
 * Every id still on `kid`, across all four tables, by the cursor the API
 * hands back.
 *
 * @returns {Promise<{ ids: Set<string>, remaining: number }>}
 */
async function idsStillOn(store, kid) {
  const ids = new Set();
  let cursor;
  let remaining = 0;

  for (let page = 0; page < RESEAL_MAX_PAGES; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const answer = await store.dispatch('courses-devices/rotationRows', { kid, cursor });
    (answer?.rows ?? []).forEach(row => {
      // Keyed by type AND id: the four tables have their own id spaces, and a
      // note that happened to share an id with a tag would otherwise mask it.
      // The type is the API's spelling, mapped to this app's — see TYPE_FROM_API.
      if (row?.id && row?.type) ids.add(`${assertKnownType(row.type)}:${row.id}`);
    });
    remaining = answer?.remaining ?? remaining;
    cursor = answer?.nextCursor;
    if (!cursor) break;
  }

  return { ids, remaining };
}

/**
 * Re-seal what is left, in batches, and report what is still outstanding.
 *
 * Never throws for a row that would not write: one row that fails must not
 * cost the rest their move, it stays on the old generation, and the next pass
 * — or the next launch — finds it again. What it will not do is claim to be
 * finished: the count comes back from the server.
 *
 * @param {object} args
 * @param {object} args.store the root Vuex store
 * @param {(progress: { done: number, total: number }) => void} [args.onProgress]
 * @param {object} [args.deps]
 * @returns {Promise<{ outcome: string, resealed: number, remaining: number }>}
 */
/**
 * Why a row this device produced could not be re-sealed, from the read path's
 * own verdict on it.
 *
 *   - `unrecoverable`: the envelope names a generation THIS device holds and
 *     still did not open — the ciphertext, not the key, is wrong. Nothing
 *     anywhere can open it (every device has the same key and the same AAD),
 *     and it has been unreadable to the member since before the sign-out.
 *   - `needs-newer-app`: a version this build cannot decode. Another build
 *     could; the member is told to update, and the row is left alone.
 *   - `other`: no key for it, or being re-encrypted elsewhere, or not an
 *     envelope at all. Not this walk's to touch.
 *
 * @param {object} row as the collector produced it
 * @returns {'unrecoverable'|'needs-newer-app'|'other'|null} null for a row
 *   that opened
 */
function whyUnopenable(row) {
  if (!cameFromEnvelope(row)) return 'other';
  if (!row?.unreadable) return null;
  if (row.unreadable === UNREADABLE_REASON.FAILED) return 'unrecoverable';
  if (row.unreadable === UNREADABLE_REASON.NEEDS_NEWER_APP) return 'needs-newer-app';
  return 'other';
}

/**
 * The shell written in place of a row that cannot be opened: every sealed
 * field of its type empty, plus the marker the read path turns into
 * UNREADABLE_REASON.UNRECOVERABLE. Under the pending key like any other
 * re-seal, so the server stops counting the row and the commit can land.
 *
 * A blank answer has one field and no shell: an empty answer IS the delete
 * (sealed-writes `sealBlank`, decision 30), and an answer nobody can read is
 * an answer that is gone.
 *
 * A tag keeps a string name, because the seal refuses a tag without one; two
 * shells in one part share that empty name and the second is refused as a
 * duplicate — counted as unwritable, and rare enough to leave there.
 */
export function shellFor(type, row) {
  if (type === ANNOTATION_TYPE.BLANK) return { ...row, answer: null };
  const shell = { id: row.id };
  SEALED_FIELDS[type].forEach(field => {
    shell[field] = null;
  });
  if (type === ANNOTATION_TYPE.TAG) shell.name = '';
  shell[UNRECOVERABLE_FIELD] = true;
  return shell;
}

export async function resealRemaining({
  store,
  onProgress = () => {},
  carryUnrecoverable = false,
  deps: overrides,
} = {}) {
  if (!store) throw new TypeError('resealRemaining needs the root Vuex store');
  const deps = { collectRows, rewriteRow, ...(overrides ?? {}) };

  const pendingKeyId = store.getters['courses-devices/pendingKeyId'];
  if (!pendingKeyId) {
    return { outcome: RESEAL_OUTCOME.NOT_ROTATING, resealed: 0, remaining: 0 };
  }

  /*
   * THE GENERATION BEING LEFT BEHIND — which depends on which way the rotation
   * is going (api#1148).
   *
   * Forwards it is `keyId`: the bundle still calls that current, because commit
   * is what moves it, so it is the old one throughout. While the rotation is
   * being UNWOUND it is `pendingKeyId`, and the walk empties that instead.
   *
   * The rest of this module does not change at all. It performs no crypto: the
   * sealing hook seals under whatever `courses-keys/writeGeneration` names, and
   * that getter follows the same flag. So "re-seal a row" is still "write it
   * back", and the direction lives in exactly these two places.
   */
  const aborting = store.getters['courses-devices/rotationAborting'];
  const oldKeyId = aborting ? pendingKeyId : store.getters['courses-devices/keyId'];
  const auth = {
    token: store.state.user?.token,
    memberId: store.state.profile?.id ?? store.getters['courses-keys/memberId'],
  };

  let resealed = 0;
  let remaining = 0;
  /*
   * ⚠️ THE ROWS THIS DEVICE CANNOT REACH, counted so a stall can be NAMED.
   *
   * The server counts every row on the old key; this walk can move only the
   * rows it can collect AND open. A row the server names that the collector
   * does not produce, or produces unreadable, is filtered out below and the
   * walk ends PARTIAL — and until now that surfaced as "some notes are still
   * being moved", for ever, with nothing saying which rows or why. Reported on
   * 2026-09-10 as a sign-out that "did not work" and a rotation that stayed
   * stuck across relaunches. What was stuck, and whether this device could
   * even see it, is the whole diagnosis; it goes out on the result.
   */
  let unreachable = 0;
  /** Of the unreachable: never produced by the collector at all. */
  let missing = 0;
  /** Of the unreachable: produced, but this device could not open them. */
  let unreadable = 0;
  /** Reached and opened, but the re-seal write was refused (a 409, a refused kid). */
  let unwritable = 0;
  /** Of the unreadable: this device holds the key they name and they still did not open. */
  let unrecoverable = 0;
  /** Of the unreadable: a version this build cannot decode. */
  let needsNewerApp = 0;
  /** Shells written in place of unrecoverable rows, this walk. */
  let carried = 0;
  /** `type:id` -> the read path's reason, for every produced row that did not open. */
  let reasons = {};

  for (let pass = 0; pass < RESEAL_MAX_PASSES; pass += 1) {
    // eslint-disable-next-line no-await-in-loop
    const asked = await idsStillOn(store, oldKeyId);
    remaining = asked.remaining;
    // ENDS ON A READ, NEVER ON A WRITE. The count is the server's, and it is
    // the same one `rotate/commit` refuses on — so this is the only answer
    // that can be trusted to mean "finished".
    if (asked.ids.size === 0) {
      return {
        outcome: RESEAL_OUTCOME.DONE,
        resealed,
        remaining,
        stuck: [],
        unreachable: 0,
        missing: 0,
        unreadable: 0,
        unwritable: 0,
        unrecoverable: 0,
        needsNewerApp: 0,
        carried,
        reasons: {},
      };
    }

    // eslint-disable-next-line no-await-in-loop
    const collected = await deps.collectRows(store, auth);
    const entries = collected.filter(
      entry =>
        asked.ids.has(`${entry.type}:${entry.row?.id}`) &&
        // ⚠️ NEVER RE-WRITE A ROW THIS DEVICE COULD NOT OPEN. The read path
        // hands back an unreadable row with its payload stripped and every
        // content field absent, so writing it back seals `{ body: null }` over
        // what it held — and an unreadable blank answer, whose content reads as
        // empty, is DELETED outright (decision 30).
        //
        // Reachable without anything being broken: a row written by a NEWER
        // build carries an envelope version this one refuses, and rotating from
        // the older phone would silently destroy the newer phone's rows. The
        // migration excludes the same rows for the same reason.
        //
        // Left on the old generation deliberately. That stalls the rotation,
        // which the screen reports and the member can act on — unlike the data
        // loss, which nothing could undo.
        cameFromEnvelope(entry.row) &&
        !entry.row?.unreadable,
    );
    // Two different failures wear one number: a row this device's lists never
    // contained (a view not drained, a lesson not listed) and a row they did
    // contain but this device could not open. The first is the collector's
    // fault and fixable here; the second is a row nobody on this phone can
    // read, and the member has to be told which they are looking at.
    /*
     * THE ROWS THAT DID NOT OPEN, BY WHY. One damaged row used to hold a
     * sign-out for ever: the server counted it, this device could not open it,
     * the commit refused on the count, and the screen said "tap again" — which
     * moved nothing, because nothing CAN open a row whose ciphertext is wrong
     * for the key it names (2026-09-11: one highlight, one member, no way out
     * short of Start over). Asked to, the walk carries such rows forward as
     * labelled shells under the new key; the member sees "damaged and could
     * not be recovered" where they already saw "could not be opened".
     */
    const stuckHere = collected.filter(
      entry => asked.ids.has(`${entry.type}:${entry.row?.id}`) && whyUnopenable(entry.row),
    );
    reasons = Object.fromEntries(
      stuckHere.map(entry => [
        `${entry.type}:${entry.row.id}`,
        entry.row?.unreadable ?? 'not-an-envelope',
      ]),
    );
    const damaged = stuckHere.filter(entry => whyUnopenable(entry.row) === 'unrecoverable');
    unrecoverable = damaged.length;
    needsNewerApp = stuckHere.filter(
      entry => whyUnopenable(entry.row) === 'needs-newer-app',
    ).length;
    const shells = carryUnrecoverable
      ? damaged.map(entry => ({ ...entry, row: shellFor(entry.type, entry.row), shell: true }))
      : [];
    const writes = [...entries, ...shells];
    const produced = collected.filter(entry => asked.ids.has(`${entry.type}:${entry.row?.id}`));
    missing = asked.ids.size - produced.length;
    unreadable = produced.length - entries.length;
    unreachable = missing + unreadable;
    // Like the two above, this describes the LAST pass: a row refused on every
    // pass is one refused row, not one per attempt.
    unwritable = 0;

    let moved = 0;
    for (let i = 0; i < writes.length; i += RESEAL_BATCH_SIZE) {
      const batch = writes.slice(i, i + RESEAL_BATCH_SIZE);
      // eslint-disable-next-line no-await-in-loop
      const results = await Promise.allSettled(batch.map(entry => deps.rewriteRow(auth, entry)));
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          moved += 1;
          if (batch[index].shell) carried += 1;
          return;
        }
        // A 409 here is another device having written the row first, which is
        // not a failure of the walk: that write went up under the pending
        // generation too, so the row has moved either way and the next pass
        // will not see it. Anything else is a write this device asked for and
        // the server refused; counted, so the detail line can say so.
        unwritable += 1;
        console.warn('[CoursesE2EE] A row could not be re-sealed:', result.reason);
      });
      onProgress({ done: resealed + moved, total: resealed + writes.length });
    }
    resealed += moved;

    // A round that moved nothing will not do better for being repeated: either
    // the rows will not write, or the enumeration cannot see what the server
    // is counting. Both are worth reporting rather than spinning on.
    if (moved === 0) break;
  }

  // Asked once more rather than subtracted. The member's other devices were
  // live throughout, and a count this walk computed would be its own opinion
  // of the number the server is about to refuse a commit on.
  const after = await idsStillOn(store, oldKeyId);
  const stuck = [...after.ids];
  if (after.remaining > 0) {
    // Ids by kind, never content: what is named here is WHICH rows are left,
    // not what they say — the same line COURSES-E2EE-014 prints when it stalls.
    console.warn(
      `[CoursesE2EE] The walk left ${after.remaining} row(s) on the old key ` +
        `(${describeStuck(stuck)}); ${missing} never appeared on this device's lists and ` +
        `${unreadable} could not be opened here; ${unwritable} write(s) were refused.`,
    );
  }
  return {
    outcome: after.remaining === 0 ? RESEAL_OUTCOME.DONE : RESEAL_OUTCOME.PARTIAL,
    resealed,
    remaining: after.remaining,
    stuck,
    unreachable,
    missing,
    unreadable,
    unwritable,
    unrecoverable,
    needsNewerApp,
    carried,
    reasons,
  };
}

/** `2 note, 1 blank` — for the console line above; screens get the counts themselves. */
function describeStuck(stuck) {
  const counts = stuck.reduce((acc, entry) => {
    const type = String(entry).split(':')[0];
    return { ...acc, [type]: (acc[type] ?? 0) + 1 };
  }, {});
  return (
    Object.entries(counts)
      .map(([type, count]) => `${count} ${type}`)
      .join(', ') || 'none listed'
  );
}
